import React, { useState, useEffect } from "react";
import {
  HStack,
  IconButton,
  ButtonGroup,
  Input,
  Select,
  Tooltip,
} from "@chakra-ui/react";
import { CSVLink } from "react-csv";
import { driverBoardTypes } from "../../../../types/Types";

// Icons
import { AddIcon } from "@chakra-ui/icons";
import { FaFileExcel } from "react-icons/fa";
import { GrRefresh } from "react-icons/gr";

interface ToolBarTypes {
  searchText: string;
  selectedView: string;
  isLoading: boolean;
  data: driverBoardTypes[];
  lastUpdated: Date | null;
  statusView:
    | "All"
    | "New"
    | "Scheduled"
    | "Showed"
    | "No Show"
    | "TDO"
    | "Seated"
    | "Upgraded"
    | "Withdrawn";
  setStatusView: (
    value:
      | "All"
      | "New"
      | "Scheduled"
      | "Showed"
      | "No Show"
      | "TDO"
      | "Seated"
      | "Upgraded"
      | "Withdrawn"
  ) => void;
  setSelectedView: (value: "All" | "Personal") => void;
  setSearchText: (value: string) => void;
  onNewFormOpen: () => void;
  onRefresh: () => void;
  subTeam: "Tasha" | "Austin" | "All" | "Kia";
    setSubTeam: (value: "Tasha" | "Austin" | "All" | "Kia") => void;
  selectedRegistration: string;
  setSelectedRegistration: (value: string) => void;
}

const ToolBar: React.FC<ToolBarTypes> = ({
  selectedView,
  searchText,
  data,
  isLoading,
  lastUpdated,
  statusView,
  setStatusView,
  setSelectedView,
  setSearchText,
  onNewFormOpen,
  onRefresh,
  subTeam,
  setSubTeam,
  selectedRegistration,
  setSelectedRegistration,
}) => {
  const isDataAvailable = data && data.length > 0;
  const [processedData, setProcessedData] = useState<driverBoardTypes[]>([]);

  useEffect(() => {
    if (data && data.length > 0) {
      // Manipulate columns and convert timezone here
      const modifiedData = data.map((item) => {
        const modifiedArrival = item.Arrival
          ? new Date(item.Arrival).toLocaleString()
          : "null";

        const modifiedCreated = item.Created
          ? new Date(item.Created).toLocaleString()
          : null;
        return {
          ...item,
          Arrival: modifiedArrival,
          Created: modifiedCreated,
        };
      });
      setProcessedData(modifiedData);
    }
  }, [data]);

  return (
    <HStack
      width="100%"
      borderRadius="2xl"
      spacing="10px"
      justifyContent="space-between"
    >
      <ButtonGroup spacing="10px">
        <Input
          variant="filled"
          placeholder="Name or ID"
          width="200px"
          focusBorderColor="orange.400"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Tooltip
          label="Add a driver to the pipeline"
          aria-label="Add New"
          openDelay={500}
        >
          <IconButton
            aria-label="Add New"
            icon={<AddIcon />}
            shadow="md"
            colorScheme="orange"
            tabIndex={-1}
            onClick={onNewFormOpen}
          />
        </Tooltip>
        <Tooltip
          label={
            lastUpdated ? `Last Updated: ${lastUpdated.toLocaleString()}` : ""
          }
          aria-label="Refresh"
          openDelay={500}
        >
          <IconButton
            aria-label="Refresh"
            icon={<GrRefresh />}
            shadow="md"
            colorScheme="blue"
            tabIndex={-1}
            onClick={onRefresh}
            isLoading={isLoading}
          />
        </Tooltip>
        <CSVLink
          data={processedData}
          filename={"driverboard.csv"}
          target="_blank"
          style={{ textDecoration: "none" }}
        >
          <Tooltip
            label="Export what you see to CSV"
            aria-label="Export to CSV"
            openDelay={500}
          >
            <IconButton
              aria-label="Export to CSV"
              icon={<FaFileExcel />}
              shadow="md"
              colorScheme="green"
              isDisabled={!isDataAvailable}
              tabIndex={-1}
            />
          </Tooltip>
        </CSVLink>
      </ButtonGroup>
      <HStack spacing="10px">
        <Select
          size="sm"
          width="100px"
          focusBorderColor="orange.400"
          variant="filled"
          value={statusView}
          onChange={(e) =>
            setStatusView(
              e.target.value as
                | "All"
                | "New"
                | "Scheduled"
                | "Showed"
                | "No Show"
                | "TDO"
                | "Seated"
                | "Upgraded"
                | "Withdrawn"
            )
          }
        >
          <option value="All">All Status</option>
          <option value="New">New</option>
          <option value="Scheduled">Scheduled</option>
          <option value="Showed">Showed</option>
          <option value="No Show">No Show</option>
          <option value="TDO">TDO</option>
          <option value="Seated">Seated</option>
          <option value="Upgraded">Upgraded</option>
          <option value="Withdrawn">Withdrawn</option>
        </Select>
        <Select
          size="sm"
          width="100px"
          focusBorderColor="orange.400"
          variant="filled"
          value={selectedRegistration}
          onChange={(e) => setSelectedRegistration(e.target.value)}
        >
          <option value="All">All Registrations</option>
          <option value="Experienced">Experienced</option>
          <option value="All Trainees">All Trainees</option>
          <option value="2 Weeks Trainee">2 Weeks Trainee</option>
          <option value="4 Weeks Trainee">4 Weeks Trainee</option>
        </Select>
        <Select
          size="sm"
          width="100px"
          focusBorderColor="orange.400"
          variant="filled"
          value={selectedView}
          onChange={(e) => {
            setSelectedView(e.target.value as "All" | "Personal");
            setSubTeam("All");
          }}
        >
          <option value="Personal">Personal</option>
          <option value="All">All Records</option>
        </Select>

        <Select
          size="sm"
          width="100px"
          isDisabled={selectedView !== "All"}
          focusBorderColor="orange.400"
          variant="filled"
          value={subTeam}
          onChange={(e) =>
              setSubTeam(e.target.value as "Tasha" | "Austin" | "All" | "Kia")
          }
        >
          <option value="All">All Team</option>
          <option value="Tasha">Tasha</option>
          <option value="Austin">Austin</option>
          <option value="Kia">Kia</option>
        </Select>
      </HStack>
    </HStack>
  );
};

export default ToolBar;
